import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, UserRound } from 'lucide-react'
import { apiClient, ApiError } from '@/lib/api'
import type { AppUser, Branch, PermissionDescriptor } from '@/lib/types'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Badge, EmptyState, Modal, Select, Toggle } from '@/components/ui/primitives'
import { t } from '@/i18n'

type Role = 'Cashier' | 'Manager' | 'Admin'

interface Draft {
  username: string
  displayName: string
  role: Role
  branchId: number | ''
  permissions: number
  isActive: boolean
  password: string
}

const empty: Draft = {
  username: '',
  displayName: '',
  role: 'Cashier',
  branchId: '',
  permissions: 0,
  isActive: true,
  password: '',
}

/** Friendlier labels than the raw enum names. */
const PERMISSION_LABELS: Record<string, string> = {
  Refund: 'Give refunds',
  VoidSale: 'Void a completed sale',
  Discount: 'Apply discounts',
  EditPrice: 'Override prices',
  OpenDrawer: 'Open the cash drawer',
  ViewReports: 'View reports',
  ViewDashboard: "See today's summary",
  ManageStock: 'Receive and adjust stock',
  RecordWaste: 'Write stock off',
  ManageUsers: 'Manage users',
  ManageSettings: 'Manage settings and branches',
  ManageCustomers: 'Manage customers',
  RecordExpense: 'Record expenses',
  ChangeExchangeRate: 'Change the LBP rate',
  ManageSafe: 'Move cash to and from the safe',
  ManageCatalog: 'Manage products and suppliers',
  CloseShift: 'Close a shift',
}

export function UsersSection() {
  const queryClient = useQueryClient()
  const [isOpen, setOpen] = useState(false)
  const [editing, setEditing] = useState<AppUser | null>(null)
  const [draft, setDraft] = useState<Draft>(empty)
  const [error, setError] = useState<string | null>(null)
  const [issuedPassword, setIssuedPassword] = useState<string | null>(null)

  const users = useQuery({
    queryKey: ['users'],
    queryFn: () => apiClient.get<AppUser[]>('/api/users'),
  })

  const permissions = useQuery({
    queryKey: ['users', 'permissions'],
    queryFn: () => apiClient.get<PermissionDescriptor[]>('/api/users/permissions'),
  })

  const branches = useQuery({
    queryKey: ['branches'],
    queryFn: () => apiClient.get<Branch[]>('/api/branches'),
  })

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        username: draft.username.trim() || null,
        displayName: draft.displayName.trim(),
        role: draft.role,
        branchId: draft.role === 'Admin' ? null : draft.branchId === '' ? null : Number(draft.branchId),
        permissions: draft.permissions,
        isActive: draft.isActive,
        password: draft.password.trim() || null,
      }

      return editing
        ? apiClient.put<{ id: number }>(`/api/users/${editing.id}`, body)
        : apiClient.post<{ id: number; temporaryPassword: string | null }>('/api/users', body)
    },
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ['users'] })
      const temporary = (result as { temporaryPassword?: string | null }).temporaryPassword
      // Shown once, because the server never stores or shows it again.
      if (temporary) setIssuedPassword(temporary)
      else setOpen(false)
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? (caught.detail ?? caught.message) : 'Could not save.'),
  })

  function open(user: AppUser | null) {
    setEditing(user)
    setIssuedPassword(null)
    setError(null)
    setDraft(
      user
        ? {
            username: user.username,
            displayName: user.displayName,
            role: user.role,
            branchId: user.branchId ?? '',
            permissions: user.permissions,
            isActive: user.isActive,
            password: '',
          }
        : empty,
    )
    setOpen(true)
  }

  /** Applying a role resets the mask to that role's defaults, which is the usual intent. */
  function applyRole(role: Role) {
    const defaults = (permissions.data ?? []).reduce((mask, permission) => {
      const isDefault =
        role === 'Admin'
          ? permission.defaultForAdmin
          : role === 'Manager'
            ? permission.defaultForManager
            : permission.defaultForCashier
      return isDefault ? mask | permission.value : mask
    }, 0)

    setDraft((current) => ({ ...current, role, permissions: defaults }))
  }

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <p className="text-sm text-[var(--text-secondary)]">
          {t('Roles set the defaults; individual rights can then be granted or taken away.')}
        </p>
        <Button size="sm" onClick={() => open(null)}>
          <Plus className="h-4 w-4" />
          {t('New')}
        </Button>
      </div>

      {users.data?.length === 0 ? (
        <EmptyState icon={UserRound} title={t('No users')} description={t('Add the people who work the till.')} />
      ) : (
        <ul className="flex flex-col gap-2">
          {users.data?.map((user) => (
            <li
              key={user.id}
              className="surface-raised flex items-center gap-3 rounded-[var(--radius-card)] border p-3"
            >
              <button
                type="button"
                onClick={() => open(user)}
                className="min-w-0 flex-1 text-left"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="truncate font-medium">{user.displayName}</span>
                  <Badge tone={user.role === 'Admin' ? 'accent' : 'neutral'}>{user.role}</Badge>
                  {!user.isActive && <Badge tone="danger">{t('Disabled')}</Badge>}
                </div>
                <p className="truncate text-xs text-[var(--text-secondary)]">
                  {/* branchName arrives as "Store — Branch" from the server. */}
                  {user.username}
                  {user.branchName ? ` · ${user.branchName}` : ' · all branches'}
                </p>
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* A password the server generated because the editor was saved with the field blank.
          Shown on its own, once, because the server keeps no copy of it. */}
      <Modal
        open={issuedPassword !== null && !isOpen}
        onClose={() => setIssuedPassword(null)}
        title={t('New password')}
        footer={<Button onClick={() => setIssuedPassword(null)}>{t('Done')}</Button>}
      >
        <p className="text-sm">
          {t('One-time password — write it down now. It is not stored and cannot be shown again.')}
        </p>
        <p className="tabular surface-sunken mt-3 rounded-lg px-4 py-3 text-center text-lg font-semibold tracking-wider">
          {issuedPassword}
        </p>
        <p className="mt-3 text-xs text-[var(--text-secondary)]">
          {t('They will be asked to change it when they first sign in.')}
        </p>
      </Modal>

      <Modal
        open={isOpen}
        onClose={() => setOpen(false)}
        wide
        title={editing ? `Edit ${editing.displayName}` : 'New user'}
        footer={
          issuedPassword ? (
            <Button onClick={() => setOpen(false)}>{t('Done')}</Button>
          ) : (
            <>
              <Button variant="secondary" onClick={() => setOpen(false)}>
                {t('Cancel')}
              </Button>
              <Button onClick={() => save.mutate()} disabled={save.isPending}>
                {t('Save')}
              </Button>
            </>
          )
        }
      >
        {issuedPassword ? (
          <div>
            <p className="text-sm">
              {t('One-time password — write it down now. It is not stored and cannot be shown again.')}
            </p>
            <p className="tabular surface-sunken mt-3 rounded-lg px-4 py-3 text-center text-lg font-semibold tracking-wider">
              {issuedPassword}
            </p>
            <p className="mt-3 text-xs text-[var(--text-secondary)]">
              {t('They will be asked to change it when they first sign in.')}
            </p>
          </div>
        ) : (
          <>
            <div className="grid gap-4 sm:grid-cols-2">
              <Input
                label={t('Name')}
                value={draft.displayName}
                onChange={(e) => setDraft({ ...draft, displayName: e.target.value })}
                required
              />
              {/* What they type at the till, and the only identifier they have. Left blank, the
                  server builds one from their name — an owner adding a cashier mid-shift should
                  not have to stop and invent one. */}
              <Input
                label={t('Username')}
                value={draft.username}
                onChange={(e) => setDraft({ ...draft, username: e.target.value })}
                hint={t('What they sign in with. Leave blank to build one from their name.')}
                autoCapitalize="none"
                spellCheck={false}
              />
              <Select
                label={t('Role')}
                value={draft.role}
                onChange={(e) => applyRole(e.target.value as Role)}
              >
                <option value="Cashier">{t('Cashier')}</option>
                <option value="Manager">{t('Manager')}</option>
                <option value="Admin">{t('Admin')}</option>
              </Select>

              {draft.role === 'Admin' ? (
                <div className="flex items-end pb-2 text-sm text-[var(--text-secondary)]">
                  {t('Admins work across every branch.')}
                </div>
              ) : (
                <Select
                  label={t('Branch')}
                  value={draft.branchId}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      branchId: e.target.value === '' ? '' : Number(e.target.value),
                    })
                  }
                >
                  <option value="">{t('Choose a branch…')}</option>
                  {branches.data?.map((branch) => (
                    <option key={branch.id} value={branch.id}>
                      {branch.displayName}
                    </option>
                  ))}
                </Select>
              )}

              <Input
                label={editing ? 'New password' : 'Password'}
                type="password"
                value={draft.password}
                onChange={(e) => setDraft({ ...draft, password: e.target.value })}
                hint={editing ? 'Leave blank to keep' : 'Leave blank to generate a one-time password'}
                autoComplete="new-password"
              />
            </div>

            <div className="mt-4">
              <Toggle
                checked={draft.isActive}
                onChange={(v) => setDraft({ ...draft, isActive: v })}
                label={t('Active')}
                hint={t('A disabled account cannot sign in but keeps its history')}
              />
            </div>

            <fieldset className="mt-5">
              <legend className="text-sm font-medium">{t('Permissions')}</legend>
              <p className="mt-0.5 mb-3 text-xs text-[var(--text-secondary)]">
                Every one of these is enforced by the server; the app only hides what it would
                refuse anyway.
              </p>
              <div className="grid gap-1 sm:grid-cols-2">
                {permissions.data?.map((permission) => (
                  <Toggle
                    key={permission.value}
                    checked={(draft.permissions & permission.value) === permission.value}
                    onChange={(checked) =>
                      setDraft((current) => ({
                        ...current,
                        permissions: checked
                          ? current.permissions | permission.value
                          : current.permissions & ~permission.value,
                      }))
                    }
                    label={PERMISSION_LABELS[permission.name] ?? permission.name}
                  />
                ))}
              </div>
            </fieldset>

            {error && (
              <p role="alert" className="mt-4 rounded-lg bg-danger-500/10 px-3 py-2 text-sm text-danger-500">
                {error}
              </p>
            )}
          </>
        )}
      </Modal>
    </div>
  )
}
